import React, { FC, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { object, string } from 'yup';
import {
  AddFormCell,
  AddPartiNewFormCont,
  CancelButton,
  EmailInfoCont,
  EmailValue,
  FormButtonsCont,
  SaveAndAddButton,
  SaveButton,
} from './AddParticipantNewForm.styles';
import { useAddNewParticipantForm } from '../../hooks/useAddNewParticipantForm';
import { IPotentialParticipantsRes } from '../../services/surveys';
import SearchDropdown from '../SearchDropdown/SearchDropdown';

interface IAddParticipantNewForm {
  setActive: (status: boolean) => void;
  groupId: string;
  updateMembersList: () => void;
  type: string;
  active?: boolean;
}

interface AddParticipantFormValues {
  participant: {
    id: string;
    name: string;
    emailAddress?: string;
  };
}

const defaultValues = {
  participant: { id: '1', name: '', emailAddress: '' },
};

const AddParticipantNewForm: FC<IAddParticipantNewForm> = ({
  active,
  groupId,
  updateMembersList,
  setActive,
  type,
}) => {
  const { t } = useTranslation();

  const addParticipantSchema = object({
    participant: object()
      .shape({
        name: string().required(t('viewSurvey.participants.modals.validations.nameIsRequired')),
      })
      .required(t('viewSurvey.participants.modals.validations.participantIsRequired')),
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setError,
    formState: { errors },
  } = useForm<AddParticipantFormValues>({
    defaultValues,
    resolver: yupResolver(addParticipantSchema),
  });

  const ref = useRef({ value: '' });
  const { surveyId } = useParams();
  const id = surveyId || '1';
  const { list, fetchPotentialParticipants, addPotentialParticipant } = useAddNewParticipantForm(
    id,
    groupId
  );

  const submitAndReset = async () => {
    const formValues = getValues('participant');
    const dataId = formValues?.id ? formValues.id : '0';
    const body = type === 'executive' ? { groupId: dataId } : { participantId: dataId };
    if (formValues.name === '') {
      return setError('participant.name', {
        type: 'custom',
        message: t('viewSurvey.participants.modals.validations.nameIsRequired'),
      }); // eslint-disable-line
    }
    await addPotentialParticipant(body);
    await updateMembersList();
    await fetchPotentialParticipants('');
    reset({ participant: { id: '1', name: '' } });
    (ref.current as { value: string }).value = '';
  };

  const submitForm = async (formValues: AddParticipantFormValues) => {
    const id = formValues?.participant?.id || '0';
    const body = type === 'executive' ? { groupId: id } : { participantId: id };
    await addPotentialParticipant(body);
    await updateMembersList();
    // if(type === 'executive'){
    //   await setUpdatedGroupsNum();
    // }
    reset({ participant: { id: '1', name: '' } });
    await fetchPotentialParticipants('');
    setActive(false);
  };

  const closeForm = () => {
    reset({ participant: { id: '1', name: '' } });
    setActive(false);
  };

  return (
    <>
      {active && (
        <AddPartiNewFormCont
          id="AddPartiNewFormCont"
          error={!!errors.participant?.name?.message}
          onSubmit={handleSubmit(submitForm)}
        >
          <AddFormCell id="AddFormCell">
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <SearchDropdown
                    customInputRef={ref}
                    width="100%"
                    items={list}
                    placeholder={t('viewSurvey.participants.form.partiFormPlaceholder')}
                    activeEl={value}
                    selectEl={data => onChange(data)}
                    searchMode
                    selectFullObject
                    doSearch={fetchPotentialParticipants}
                    error={errors.participant?.name?.message}
                    additionalBodyInfoKey={type === 'team' ? 'emailAddress' : undefined}
                  />
                  <EmailInfoCont>
                    <EmailValue>{value?.emailAddress}</EmailValue>
                  </EmailInfoCont>
                </>
              )}
              name="participant"
            />
          </AddFormCell>
          <FormButtonsCont id="FormButtonsCont">
            <CancelButton id="CancelButton" onClick={closeForm}>
              {t('viewSurvey.participants.form.cancel')}
            </CancelButton>
            <SaveAndAddButton
              data-testid="savePartiFormAndAdd"
              id="SaveAndAddButton"
              type="button"
              onClick={submitAndReset}
            >
              {t('viewSurvey.participants.form.saveAndAdd')}
            </SaveAndAddButton>
            <SaveButton data-testid="savePartiForm" id="SaveButton" type="submit">
              {' '}
              {t('viewSurvey.participants.form.save')}
            </SaveButton>
          </FormButtonsCont>
        </AddPartiNewFormCont>
      )}
    </>
  );
};

export default AddParticipantNewForm;
